import { useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';
import I18n from 'i18n-js';
import { ticket_comment_images_path } from 'js-routes/generated/routes';

const IMAGE_MIME_PATTERN = /^image\//;
const PLACEHOLDER = '![uploading...](#)';

const extractImageFile = (source) => {
  const file = Array.from(source?.files ?? [])
    .find(({ type }) => IMAGE_MIME_PATTERN.test(type));
  if (file) return file;

  const item = Array.from(source?.items ?? [])
    .find(({ type, kind }) => kind === 'file' && IMAGE_MIME_PATTERN.test(type));
  return item?.getAsFile() ?? null;
};

const insertAtCursor = (textArea, snippet) => {
  const start = textArea.selectionStart ?? textArea.value.length;
  const end = textArea.selectionEnd ?? textArea.value.length;
  const before = textArea.value.slice(0, start);
  const after = textArea.value.slice(end);
  return {
    nextValue: `${before}${snippet}${after}`,
    nextCursor: start + snippet.length,
  };
};

const useEmbedImage = ({ ticketId, form, fieldName }) => {
  const embedFile = useCallback(async (file, textArea) => {
    const { nextValue, nextCursor } = insertAtCursor(textArea, PLACEHOLDER);
    form.setFieldsValue({ [fieldName]: nextValue });

    const formData = new FormData();
    formData.append('ticket_id', ticketId);
    formData.append('image', file);

    try {
      const { data } = await axios.post(ticket_comment_images_path(), formData);
      const markdown = `![](${data.url})`;
      const finalValue = nextValue.replace(PLACEHOLDER, markdown);
      form.setFieldsValue({ [fieldName]: finalValue });
      requestAnimationFrame(() => {
        textArea.focus();
        const finalCursor = nextCursor - PLACEHOLDER.length + markdown.length;
        textArea.setSelectionRange(finalCursor, finalCursor);
      });
    } catch {
      const rolledBack = nextValue.replace(PLACEHOLDER, '');
      form.setFieldsValue({ [fieldName]: rolledBack });
      message.error(I18n.t('flash.ticket_comments.image_upload_failed'));
    }
  }, [ticketId, form, fieldName]);

  const handlePaste = useCallback((event) => {
    const file = extractImageFile(event.clipboardData);
    if (!file) return;

    event.preventDefault();
    embedFile(file, event.target);
  }, [embedFile]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event) => {
    const file = extractImageFile(event.dataTransfer);
    if (!file) return;

    event.preventDefault();
    embedFile(file, event.target);
  }, [embedFile]);

  return { handlePaste, handleDragOver, handleDrop };
};

export default useEmbedImage;
